import { useApiErrorToast } from 'app/api/app-api/apiHooksHelpers';
import { useSendTransferEmailMutation } from 'app/modules/organizations/api/organizationsApiHooks';
import { useFormik } from 'formik';
import { useEffect } from 'react';
import { Box, Button, FormGroup, InlineText, Input, Modal, ModalActions, ModalContent, ModalHeader, ModalLayout, Text, useToasts } from 'tombac';
import isEmail from 'validator/lib/isEmail';
import { object, string } from 'yup';

/**
 * Base props for the InviteToOrganizationModal component.
 */

/**
 * Props for the InviteToOrganizationModal component that is read-only.
 */

/**
 * Props for the InviteToOrganizationModal component that can be edited.
 */

/**
 * Props for the InviteToOrganizationModal component.
 */
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const validationSchema = object({
  email: string().ensure().trim().required('Email is required.').test({
    name: 'email',
    message: `Invalid email address.`,
    test: value => value !== '' && isEmail(value)
  })
});
/**
 * Represents a modal component for inviting a user to join an organization.
 */
export const InviteToOrganizationModal = ({
  isOpen,
  muteToasts = false,
  userEmail = '',
  organization,
  onError,
  onInviteSent,
  onRequestClose,
  readOnly = false
}) => {
  const addApiErrorToast = useApiErrorToast();
  const {
    addToast
  } = useToasts();
  const {
    mutateAsync: sendTransferEmail
  } = useSendTransferEmailMutation();
  const {
    errors,
    getFieldProps,
    handleSubmit,
    isSubmitting,
    resetForm,
    touched
  } = useFormik({
    initialValues: {
      email: userEmail
    },
    validateOnMount: true,
    validationSchema,
    async onSubmit({
      email
    }) {
      try {
        await sendTransferEmail({
          requestedUser: email,
          organizationId: organization.id
        });
        !muteToasts && addToast('Email has been send to the user', 'success');
        onInviteSent?.();
      } catch (error) {
        !muteToasts && addApiErrorToast(error);
        onError?.(error);
      }
    }
  });

  // Reset form when modal is opened
  useEffect(() => {
    if (isOpen) {
      resetForm({
        values: {
          email: userEmail
        }
      });
    }
  }, [isOpen]);
  return /*#__PURE__*/_jsx(Modal, {
    isOpen: isOpen,
    "data-sentry-element": "Modal",
    "data-sentry-component": "InviteToOrganizationModal",
    "data-sentry-source-file": "InviteToOrganizationModal.tsx",
    children: /*#__PURE__*/_jsxs(ModalLayout, {
      as: "form",
      onSubmit: handleSubmit,
      "data-sentry-element": "ModalLayout",
      "data-sentry-source-file": "InviteToOrganizationModal.tsx",
      "data-sentry-element": "ModalLayout",
      children: [/*#__PURE__*/_jsx(ModalHeader, {
        "data-sentry-element": "ModalHeader",
        "data-sentry-source-file": "InviteToOrganizationModal.tsx",
        "data-sentry-element": "ModalHeader",
        children: "Invite user to join the organization"
      }), /*#__PURE__*/_jsxs(ModalContent, {
        "data-sentry-element": "ModalContent",
        "data-sentry-source-file": "InviteToOrganizationModal.tsx",
        "data-sentry-element": "ModalContent",
        children: [readOnly ? /*#__PURE__*/_jsxs("div", {
          children: [/*#__PURE__*/_jsx(InlineText, {
            children: "User:"
          }), ' ', /*#__PURE__*/_jsx(InlineText, {
            fontWeight: 600,
            children: userEmail
          }), /*#__PURE__*/_jsx(Input, {
            type: "hidden",
            ...getFieldProps('email')
          })]
        }) : /*#__PURE__*/_jsx(FormGroup, {
          $width: "100%",
          error: touched.email && errors.email,
          label: "Email",
          children: /*#__PURE__*/_jsx(Input, {
            invalid: Boolean(touched.email && errors.email),
            ...getFieldProps('email')
          })
        }), /*#__PURE__*/_jsxs(Box, {
          $mt: "1sp",
          "data-sentry-element": "Box",
          "data-sentry-source-file": "InviteToOrganizationModal.tsx",
          "data-sentry-element": "Box",
          children: [/*#__PURE__*/_jsx(InlineText, {
            "data-sentry-element": "InlineText",
            "data-sentry-source-file": "InviteToOrganizationModal.tsx",
            "data-sentry-element": "InlineText",
            children: "Organization:"
          }), ' ', /*#__PURE__*/_jsx(InlineText, {
            fontWeight: 600,
            "data-sentry-element": "InlineText",
            "data-sentry-source-file": "InviteToOrganizationModal.tsx",
            "data-sentry-element": "InlineText",
            children: organization?.name
          })]
        }), /*#__PURE__*/_jsx(Text, {
          $mt: "3sp",
          variant: "secondary",
          "data-sentry-element": "Text",
          "data-sentry-source-file": "InviteToOrganizationModal.tsx",
          "data-sentry-element": "Text",
          children: "Please note: The user will be added to the organization once the invitation is confirmed."
        })]
      }), /*#__PURE__*/_jsxs(ModalActions, {
        $mb: "2sp",
        "data-sentry-element": "ModalActions",
        "data-sentry-source-file": "InviteToOrganizationModal.tsx",
        "data-sentry-element": "ModalActions",
        children: [/*#__PURE__*/_jsx(Button, {
          onClick: onRequestClose,
          type: "button",
          variant: "flat",
          "data-sentry-element": "Button",
          "data-sentry-source-file": "InviteToOrganizationModal.tsx",
          "data-sentry-element": "Button",
          children: "Cancel"
        }), /*#__PURE__*/_jsx(Button, {
          busy: isSubmitting,
          type: "submit",
          variant: "primary",
          "data-sentry-element": "Button",
          "data-sentry-source-file": "InviteToOrganizationModal.tsx",
          "data-sentry-element": "Button",
          children: "Invite"
        })]
      })]
    })
  });
};